import type { AsyncDuckDB, DuckDBConfig, DuckDBBundle, Logger, LogLevel, DuckDBDataProtocol } from '@duckdb/duckdb-wasm';

/**
 * DuckDB WASM module interface
 */
export interface DuckDBModule {
  LogLevel: typeof LogLevel;
  DuckDBDataProtocol: typeof DuckDBDataProtocol;
  AsyncDuckDB: typeof AsyncDuckDB;
  ConsoleLogger: new (logLevel: LogLevel) => Logger;
  getJsDelivrBundles: () => any;
  selectBundle: (bundles: any) => Promise<DuckDBBundle>;
  createWorker: (worker: string) => Promise<Worker>;
}

/**
 * Configuration for DuckDB engine initialization
 */
export type DuckDBEngineConfig = DuckDBConfig;

// Cache the bundle promise to avoid multiple fetches
let __bundle: Promise<DuckDBBundle> | undefined = undefined;
let __duckdb: DuckDBModule | undefined = undefined;

/**
 * Get the DuckDB module
 * @returns Promise resolving to the DuckDB module
 */
export async function getDuckDB(): Promise<DuckDBModule> {
  if (__duckdb) {
    return __duckdb;
  }
  
  const duckdbModule = await import('@duckdb/duckdb-wasm');
  __duckdb = duckdbModule as unknown as DuckDBModule;
  return __duckdb;
}

/**
 * Get the DuckDB bundle, cached for performance
 * @returns Promise resolving to the DuckDB bundle
 */
export async function getBundle(): Promise<DuckDBBundle> {
  if (__bundle) {
    return __bundle;
  }
  
  const duckdb = await getDuckDB();
  const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
  __bundle = Promise.resolve(duckdb.selectBundle(JSDELIVR_BUNDLES));
  
  return __bundle;
}

/**
 * Check if the current environment is Node.js (server-side)
 * @returns True if running in Node.js environment
 */
export function isNodeEnvironment(): boolean {
  return typeof window === 'undefined';
}

/**
 * Create a mock DuckDB instance for testing in Node.js environment
 * @returns Mock AsyncDuckDB instance with basic functionality
 */
export function createMockDuckDB(): AsyncDuckDB {
  return {
    // Mock methods used in tests
    connect: async () => ({
      query: async () => {},
      close: async () => {}
    }),
    registerFileBuffer: async () => {},
    instantiate: async () => {},
    registerFileHandle: async () => {},
    open: async () => {},
    terminate: async () => {},
    copyFileToBuffer: async () => new ArrayBuffer(0),
    dropFile: async () => {}
  } as unknown as AsyncDuckDB;
}

/**
 * Initialize a DuckDB instance for browser environment
 * @param params Configuration object for initialization
 * @returns Promise resolving to initialized AsyncDuckDB instance
 */
export async function initializeDuckDBForBrowser(params: {
  config?: DuckDBEngineConfig;
}): Promise<AsyncDuckDB> {
  const { config } = params;
  
  // Get the bundle
  const bundle = await getBundle();
  
  // Create the worker
  if (!bundle.mainWorker) {
    throw new Error('Bundle main worker is null or undefined');
  }
  
  const duckdb = await getDuckDB();
  
  // Use the worker URL directly instead of creating a Blob
  const worker = await duckdb.createWorker(bundle.mainWorker);
  
  // Create DuckDB instance with console logger
  const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
  const db = new duckdb.AsyncDuckDB(logger, worker);
  
  // Instantiate DuckDB with the bundle
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
  
  // Open the database with optimized configuration
  await db.open({
    path: ":memory:", 
    query: {
      castBigIntToDouble: true,
      castDecimalToDouble: true,
      castTimestampToDate: true,
      castDurationToTime64: true,
      ...config?.query // Allow overriding default query config
    },
    ...config // Allow overriding other config options
  });

  return db;
}

/**
 * Initialize DuckDB instance based on the current environment
 * @param params Configuration object for initialization
 * @returns Promise resolving to initialized AsyncDuckDB instance
 */
export async function initializeDuckDB(params: {
  config?: DuckDBEngineConfig;
} = {}): Promise<AsyncDuckDB> {
  const { config } = params;
  
  try {
    // In Node.js environment, use mock for testing
    if (isNodeEnvironment()) {
      return createMockDuckDB();
    }

    // In browser environment, initialize with full DuckDB WASM
    return await initializeDuckDBForBrowser({ config });
    
  } catch (error) {
    console.error('Error during DuckDB initialization:', error);
    throw new Error(`Failed to initialize DuckDB: ${error}`);
  }
}

/**
 * Reset the cached module and bundle (useful for testing)
 */
export function resetCache(): void {
  __bundle = undefined;
  __duckdb = undefined;
} 